import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ValorizaBrandMark } from '../components/ValorizaBrandMark';

// Animated SVG Clock Component
function AnimatedClock() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const secondDeg = (time.getSeconds() / 60) * 360;
  const minuteDeg = ((time.getMinutes() + time.getSeconds() / 60) / 60) * 360;
  const hourDeg   = ((time.getHours() % 12 + time.getMinutes() / 60) / 12) * 360;

  return (
    <div style={{ position: 'relative', width: '280px', height: '280px' }}>
      {/* Outer glow ring */}
      <div style={{
        position: 'absolute', inset: '-20px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(96,165,250,0.15) 0%, transparent 70%)',
        animation: 'pulse-glow 3s ease-in-out infinite'
      }} />

      <svg viewBox="0 0 200 200" width="280" height="280" style={{ position: 'relative', zIndex: 1 }}>
        {/* Background circle */}
        <circle cx="100" cy="100" r="96" fill="#0f172a" stroke="#1e40af" strokeWidth="2" opacity="0.9" />
        <circle cx="100" cy="100" r="90" fill="none" stroke="#334155" strokeWidth="0.5" />

        {/* Hour markers */}
        {[...Array(12)].map((_, i) => {
          const angle = (i * 30 - 90) * (Math.PI / 180);
          const x1 = 100 + 80 * Math.cos(angle);
          const y1 = 100 + 80 * Math.sin(angle);
          const x2 = 100 + 72 * Math.cos(angle);
          const y2 = 100 + 72 * Math.sin(angle);
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={i % 3 === 0 ? '#60a5fa' : '#475569'}
              strokeWidth={i % 3 === 0 ? 2.5 : 1}
              strokeLinecap="round" />
          );
        })}

        {/* Minute markers */}
        {[...Array(60)].map((_, i) => {
          if (i % 5 === 0) return null;
          const angle = (i * 6 - 90) * (Math.PI / 180);
          const x1 = 100 + 82 * Math.cos(angle);
          const y1 = 100 + 82 * Math.sin(angle);
          const x2 = 100 + 78 * Math.cos(angle);
          const y2 = 100 + 78 * Math.sin(angle);
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#1e3a5f" strokeWidth="0.8" strokeLinecap="round" />
          );
        })}

        {/* Digital time display */}
        <text x="100" y="140" textAnchor="middle" fill="#94a3b8"
          style={{ fontFamily: 'IBM Plex Mono', fontSize: '10px' }}>
          {time.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </text>

        {/* Hour hand */}
        <line
          x1="100" y1="100"
          x2={100 + 45 * Math.cos((hourDeg - 90) * Math.PI / 180)}
          y2={100 + 45 * Math.sin((hourDeg - 90) * Math.PI / 180)}
          stroke="#e2e8f0" strokeWidth="4" strokeLinecap="round"
          style={{ transition: 'all 0.5s cubic-bezier(0.4,2.08,0.55,0.44)' }}
        />

        {/* Minute hand */}
        <line
          x1="100" y1="100"
          x2={100 + 62 * Math.cos((minuteDeg - 90) * Math.PI / 180)}
          y2={100 + 62 * Math.sin((minuteDeg - 90) * Math.PI / 180)}
          stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round"
          style={{ transition: 'all 0.5s cubic-bezier(0.4,2.08,0.55,0.44)' }}
        />

        {/* Second hand */}
        <line
          x1="100" y1="110"
          x2={100 + 70 * Math.cos((secondDeg - 90) * Math.PI / 180)}
          y2={100 + 70 * Math.sin((secondDeg - 90) * Math.PI / 180)}
          stroke="#34d399" strokeWidth="1.5" strokeLinecap="round"
          style={{ transition: 'all 0.2s cubic-bezier(0.4,2.08,0.55,0.44)' }}
        />

        {/* Center dot */}
        <circle cx="100" cy="100" r="5" fill="#60a5fa" />
        <circle cx="100" cy="100" r="2" fill="#0f172a" />
      </svg>
    </div>
  );
}

export default function LoginPage() {
  const { loginEmergency, loginSSO, isAuthenticated, ssoError, clearSsoError } = useAuth();
  const navigate = useNavigate();
  const { t, lang, toggleLang } = useLang();
  const [showEmergency, setShowEmergency] = useState(false);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [error, setError]       = useState('');
  const [date, setDate]         = useState(new Date());

  // Surface any SSO callback error coming from AuthContext
  useEffect(() => {
    if (ssoError) setError(ssoError);
  }, [ssoError]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const interval = setInterval(() => setDate(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const handleEmergencyLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (clearSsoError) clearSsoError();
    try {
      await loginEmergency(email, password);
    } catch (err) {
      setError(err.message || t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSsoLogin = async () => {
    setError('');
    if (clearSsoError) clearSsoError();
    setSsoLoading(true);
    try {
      // This redirects the page to Microsoft. The callback is handled in AuthContext.
      await loginSSO();
    } catch (err) {
      setError(err.message || 'No se pudo iniciar el SSO de Microsoft');
      setSsoLoading(false);
    }
  };

  const dateStr = date.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      background: '#0b1220',
      position: 'relative', overflow: 'hidden'
    }}>
      {/* Decorative radial background (<=20% viewport) */}
      <div style={{
        position: 'absolute', top: '10%', left: '15%',
        width: '300px', height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', bottom: '5%', right: '30%',
        width: '200px', height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(52,211,153,0.08) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />

      {/* Language toggle */}
      <button
        onClick={toggleLang}
        style={{
          position: 'absolute', top: '20px', right: '20px',
          background: '#1e293b', border: '1px solid #334155',
          color: '#94a3b8', borderRadius: '8px', padding: '6px 12px',
          cursor: 'pointer', fontSize: '12px', fontFamily: 'IBM Plex Mono',
          transition: 'background-color 200ms'
        }}
      >
        {lang === 'es' ? 'EN' : 'ES'}
      </button>

      {/* Left Panel - Animation */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '60px 40px',
          background: '#0d1a2e',
          borderRight: '1px solid #1e3a5f'
        }}
        className="hidden lg:flex"
      >
        {/* Valoriza Logo and Product Name */}
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <ValorizaBrandMark placement="login" showPoweredBy={false} />
        </div>

        {/* Animated Clock */}
        <div style={{ marginBottom: '40px' }}>
          <AnimatedClock />
        </div>

        {/* Date */}
        <div style={{ color: '#60a5fa', fontSize: '14px', marginBottom: '24px', fontFamily: 'IBM Plex Mono', textAlign: 'center' }}>
          {dateStr}
        </div>

        {/* Claims */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '360px' }}>
          {[
            { icon: '🔐', text: 'SSO Azure AD' },
            { icon: '📊', text: '3.7M fichajes procesados' },
            { icon: '⚡', text: 'KPIs en tiempo real' },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.1 }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: '#1e293b', border: '1px solid #1e3a5f',
                borderRadius: '10px', padding: '10px 16px'
              }}
            >
              <span style={{ fontSize: '16px' }}>{item.icon}</span>
              <span style={{ color: '#94a3b8', fontSize: '13px' }}>{item.text}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Right Panel - Login Form */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{
          width: '100%', maxWidth: '480px', minWidth: '340px',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '60px 48px',
          background: '#0b1220'
        }}
      >
        {/* Mobile Valoriza Logo */}
        <div className="flex lg:hidden" style={{ marginBottom: '32px' }}>
          <ValorizaBrandMark placement="login" showPoweredBy={false} />
        </div>

        <div style={{ width: '100%', maxWidth: '360px' }}>
          <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '28px', fontWeight: 700, color: '#f1f5f9', marginBottom: '8px', lineHeight: 1.2 }}>
            {t('loginTitle')}
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '40px', lineHeight: 1.6 }}>
            {t('loginSub')}
          </p>

          {/* Microsoft SSO Button */}
          <button
            data-testid="login-microsoft-sso-button"
            onClick={handleSsoLogin}
            disabled={ssoLoading}
            style={{
              width: '100%', height: '48px',
              background: ssoLoading ? '#1e40af' : '#2563eb',
              border: 'none', borderRadius: '12px',
              color: 'white', fontSize: '15px',
              fontFamily: 'Space Grotesk', fontWeight: 600,
              cursor: ssoLoading ? 'wait' : 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', gap: '10px',
              marginBottom: '24px',
              transition: 'filter 200ms, box-shadow 200ms',
              boxShadow: '0 0 0 0 rgba(37,99,235,0)',
              opacity: ssoLoading ? 0.85 : 1
            }}
            onMouseEnter={e => { if (!ssoLoading) { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,99,235,0.4)'; } }}
            onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.boxShadow = '0 0 0 0 rgba(37,99,235,0)'; }}
          >
            {/* Microsoft Icon */}
            <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            {ssoLoading ? 'Redirigiendo a Microsoft...' : t('loginSsoBtn')}
          </button>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{ flex: 1, height: '1px', background: '#1e293b' }} />
            <span style={{ color: '#334155', fontSize: '12px' }}>o</span>
            <div style={{ flex: 1, height: '1px', background: '#1e293b' }} />
          </div>

          {/* SSO error banner (visible even without opening emergency dialog) */}
          {error && !showEmergency && (
            <div
              data-testid="login-sso-error"
              style={{
                color: '#fca5a5', fontSize: '13px',
                background: '#1c0a0a', border: '1px solid #7f1d1d',
                padding: '10px 12px', borderRadius: '8px',
                marginBottom: '16px', lineHeight: 1.4,
              }}
            >
              {error}
            </div>
          )}

          {/* Emergency Access Link */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <button
              data-testid="login-emergency-access-link"
              onClick={() => setShowEmergency(true)}
              style={{
                background: 'none', border: 'none',
                color: '#334155', fontSize: '12px',
                cursor: 'pointer', textDecoration: 'underline',
                fontFamily: 'Inter', letterSpacing: '0.01em'
              }}
            >
              {t('loginEmergency')}
            </button>
          </div>

          {/* Trust badges */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '32px', flexWrap: 'wrap' }}>
            {['Azure AD', 'SFTP Seguro', 'GDPR'].map(label => (
              <span key={label} style={{
                background: '#0f172a', border: '1px solid #1e293b',
                color: '#475569', fontSize: '11px',
                padding: '4px 10px', borderRadius: '20px',
                fontFamily: 'IBM Plex Mono'
              }}>{label}</span>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Emergency Login Dialog */}
      <Dialog open={showEmergency} onOpenChange={setShowEmergency}>
        <DialogContent style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', maxWidth: '400px' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk', color: '#f1f5f9' }}>Acceso de emergencia</DialogTitle>
          </DialogHeader>
          <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
            Solo para administradores. Use el SSO de Microsoft siempre que sea posible.
          </p>
          <form onSubmit={handleEmergencyLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <Label htmlFor="emergency-email" style={{ color: '#94a3b8', fontSize: '13px' }}>{t('loginEmail')}</Label>
              <Input
                id="emergency-email"
                data-testid="login-emergency-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@valoriza.es"
                required
                style={{ marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
              />
            </div>
            <div>
              <Label htmlFor="emergency-pass" style={{ color: '#94a3b8', fontSize: '13px' }}>{t('loginPassword')}</Label>
              <Input
                id="emergency-pass"
                data-testid="login-emergency-password-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={{ marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
              />
            </div>
            {error && (
              <p style={{ color: '#ef4444', fontSize: '13px', background: '#1c0a0a', border: '1px solid #7f1d1d', padding: '8px 12px', borderRadius: '8px' }}>
                {error}
              </p>
            )}
            <Button
              data-testid="login-emergency-submit-button"
              type="submit"
              disabled={loading}
              style={{ background: '#1d4ed8', color: 'white', height: '44px', borderRadius: '10px' }}
            >
              {loading ? 'Verificando...' : t('loginSubmit')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
